import { Controller } from "@hotwired/stimulus"

// Filters an already-rendered list in place as the user types.
//
// A term matches an item when it is a substring of the item's
// `data-list-filter-text` anywhere in the text — so "fifth" finds
// "My Storage Rentals Fifth Street" without an exact prefix. Whitespace splits
// the query into terms that must all match, in any order. An optional `count`
// target reports how many items are showing, as "matched of total".
export default class extends Controller {
  static targets = ["count", "empty", "input", "item"]

  #filterQueued = false

  connect() {
    this.filter()
  }

  // Applies the current query to items swapped in later, e.g. by a reloaded turbo frame.
  // Stimulus calls this once per item, so a swap of many rows is batched into one filter.
  itemTargetConnected() {
    if (this.#filterQueued) return

    this.#filterQueued = true
    queueMicrotask(() => {
      this.#filterQueued = false
      this.filter()
    })
  }

  filter() {
    const terms = this.hasInputTarget
      ? this.inputTarget.value.toLowerCase().split(/\s+/).filter(Boolean)
      : []

    let matched = 0

    this.itemTargets.forEach((item) => {
      const text = (item.dataset.listFilterText || item.textContent).toLowerCase()
      const hit = terms.every((term) => text.includes(term))

      // Items carry their own display utility (`flex`), which outranks `[hidden]` in the
      // cascade, so they hide by class. The empty state carries none and hides by attribute,
      // which keeps it genuinely invisible to drivers that don't apply our stylesheet.
      item.classList.toggle("hidden", !hit)
      if (hit) matched++
    })

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${matched} of ${this.itemTargets.length}`
    }

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = matched > 0 || terms.length === 0
    }

    this.dispatch("filtered")
  }
}
